/**
 * The prices the strategy is watching, drawn so they cannot be read as orders.
 *
 * This is the most expensive misreading this screen can cause. The normal grid
 * is resting limit orders: they exist at the venue and will fill without
 * anybody deciding anything. The recovery zones are not orders — no capital is
 * committed at them and nothing happens there until the runtime decides it
 * does. Drawn the same way, an operator would believe the account has already
 * placed four more buys and sized their risk accordingly.
 *
 * So they are a separate block with its own words. A zone is `ожидает`,
 * `взведена`, `следит` or `готова к докупке`; an order is `выставлен`,
 * `исполнен`, `исполнен частично` or `снят`. No status is shared between the
 * two vocabularies, which is what makes the difference impossible to miss.
 *
 * Absent until the runtime reports zones. This block never draws the contract's
 * specified zones as if they were live: the contract says where they would be,
 * and only the runtime knows where they are.
 */
import { price } from "@/shared/money/money";
import { RUNG_STATUS_LABEL, kindOfStatus, type Rung } from "@/shared/strategy/recovery";

const ABSENT = "—";

const TONE: Record<string, string> = {
  WAITING: "border-border-strong text-text-muted",
  ARMED: "border-status-warning text-status-warning",
  TRACKING: "border-status-warning text-status-warning",
  DCA_READY: "border-status-warning text-status-warning",
};

export function RecoveryZones({
  rungs,
  slotId,
}: {
  readonly rungs: readonly Rung[];
  readonly slotId: string;
}): React.JSX.Element | null {
  const zones = rungs.filter((rung) => kindOfStatus(rung.status) === "zone");
  if (zones.length === 0) return null;

  return (
    <section data-testid={`recovery-zones-${slotId}`} className="min-w-0">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="text-label-caps tracking-[0.08em] text-text-muted">
          Зоны восстановления
        </span>
        {/* Said on the block itself, not in a legend somewhere else. A reader
            who has to look elsewhere to learn that these are not orders has
            already read them as orders. */}
        <span className="text-helper-sm text-text-muted">ордера не выставлены</span>
      </div>
      <ul className="flex flex-wrap gap-2">
        {zones.map((zone) => (
          <li
            key={zone.index}
            data-testid={`recovery-zone-${slotId}-${String(zone.index)}`}
            data-status={zone.status}
            className={`flex min-w-0 flex-col rounded-row border border-dashed px-2 py-1 ${
              TONE[zone.status] ?? "border-border-strong text-text-muted"
            }`}
          >
            <span className="font-numeric text-helper-sm tabular-nums whitespace-nowrap">
              {zone.offset ?? (zone.price === null ? ABSENT : price(zone.price))}
            </span>
            <span className="text-helper-sm whitespace-nowrap">
              {RUNG_STATUS_LABEL[zone.status]}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The rungs a slot payload carries, or none.
 *
 * Read defensively for the same reason the recovery reading is: the running
 * payload does not carry them yet, and widening the generated type would claim
 * a promise the server has not made.
 */
export function rungsOf(slot: object): readonly Rung[] {
  const raw = (slot as { rungs?: unknown }).rungs;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): Rung[] => {
    if (typeof entry !== "object" || entry === null) return [];
    const record = entry as Record<string, unknown>;
    const status = record.status;
    if (typeof status !== "string" || !(status in RUNG_STATUS_LABEL)) return [];
    const typed = status as Rung["status"];
    const index = typeof record.index === "number" ? record.index : 0;
    const text = (value: unknown): string | null =>
      typeof value === "string" && value !== "" ? value : null;
    return [
      {
        index,
        kind: kindOfStatus(typed),
        status: typed,
        price: text(record.price),
        offset: text(record.offset),
      },
    ];
  });
}
